import { cx } from '@/lib/cx'

interface Option<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  label: string
  options: Option<T>[]
  value: T
  onChange: (value: T) => void
}

/**
 * One control, not three buttons in a row: separate bordered boxes read as
 * three independent things to press.
 */
export function SegmentedControl<T extends string>({ label, options, value, onChange }: Props<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex items-stretch overflow-hidden rounded-md border border-control bg-surface"
    >
      {options.map((option) => {
        const selected = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            // `basis-auto`, never a zero basis: with one the labels contribute
            // nothing to the track's intrinsic width and get clipped by its own
            // overflow.
            className={cx(
              'min-h-[calc(var(--spacing-field)-2px)] min-w-0 flex-1 basis-auto px-3 text-sm transition-colors duration-150 ease-standard not-first:border-l not-first:border-line focus-visible:outline-offset-[-3px]',
              selected
                ? 'bg-accent-soft font-semibold text-accent'
                : 'font-medium text-fg-muted hover:bg-hover hover:text-fg',
            )}
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
